import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConstants } from '../constants';
import { FormArray, Validators } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { IFormFields } from '../form-fields';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import { FileValidator } from '../file-input-validator';

declare var $: any;

@Injectable({
    providedIn: 'root'
})
export class ConfigService {

    constructor(private http: HttpClient) { }

    // Url to get initial fields (JSON format) to be loaded in the form
    private urlToGetEditFields: string = $("#hdnFormLoadUrl").val() + '?formId=' + $("#hdnFormId").val() + '&formType=' + $("#hdnFormType").val();

    // Url to get render fields (JSON format) to be loaded in the form
    private urlToGetRenderFields: string = $("#hdnFormLoadUrl").val() + '?formId=' + $("#hdnFormId").val() + '&formType=' + $("#hdnFormType").val() + "&cacheKey=" + encodeURIComponent($("#hdnCompanyID").val());

    originalConfig: any[] = [];
    fileUploadEvents = {};

    newConfigFields = AppConstants.NEW_CONFIG_FIELDS;

    allCheckboxFieldValues: any = {};
    uploadedFiles: any = [];

    private formFields = [
        { name: 'Text Input', value: 'text' },
        { name: 'Paragraph', value: 'textarea' },
        { name: 'Select', value: 'select' },
        { name: 'Email', value: 'email' },
        { name: 'Phone', value: 'tel' },
        { name: 'Checkbox', value: 'checkbox' },
        { name: 'Radio Button', value: 'radio' },
        { name: 'File Upload', value: 'file' },
        { name: 'Terms Checkbox', value: 'terms' },
        { name: 'Informational Message', value: 'infoMessage' }
        ///{ name: 'Date', value: 'date' },
        ///{ name: 'Password', value: 'password' },
        ///{ name: 'Link', value: 'url' },
        ///{ name: 'Number', value: 'number' },
    ];

    get formFieldsToAdd() {
        return this.formFields;
    }


    // Configuring fields (in JSON form) as a Behavior subject and casting it
    // so that the child components get the latest value of the fields.
    // Behavior Subjects: When you subscribe to a behavior subject,
    //                    it will give you the last emitted value right away.
    private config = new BehaviorSubject<any>(this.originalConfig);
    cast = this.config.asObservable();

    // Every time this function is called, the modified value of "config" changes and is casted
    editConfig(newConfig) {
        const modifiedConfig = newConfig.map(this.addValidators);
        this.config.next(modifiedConfig);
    }

    // Get your form fields to be edited from your API (as an observable)
    getEditFormFields(): Observable<IFormFields> {
        return this.http
            .get<IFormFields>(this.urlToGetEditFields)
            .catch(this.errorHandler);
    }

    // Get your form fields to be edited from your API (as an observable)
    getRenderFormFields(): Observable<IFormFields> {
        return this.http
            .get<IFormFields>(this.urlToGetRenderFields)
            .catch(this.errorHandler);
    }

    errorHandler(error: HttpErrorResponse) {
        return Observable.throw(error.message || 'Server error when fetching form fields!');
    }

    addValidators(fieldConfig) {
        fieldConfig['validators'] = [];
        if (fieldConfig.validatorInfo.required && fieldConfig.type === 'file') {
            //fieldConfig['validators'].push(FileValidator.validate);
        } else if (fieldConfig.validatorInfo.required) {
            fieldConfig['validators'].push(Validators.required);
        }
        if (fieldConfig.validatorInfo.email) {
            fieldConfig['validators'].push(Validators.email);
        }
        return fieldConfig;
    }

    // function to get the values selected in the checkbox with multiple options.
    // This would help us get the actual value when you select the options
    // and not 'true' or 'false' values.
    getSelectedCheckboxValues(form, field) {
        const checkboxValues = [];
        const checkboxArray = <FormArray>form.get(field.name);
        checkboxArray.controls.forEach((control, i) => {
            if (control.value === true) {
                checkboxValues.push(field.choices[i]);
            }
        });
        this.allCheckboxFieldValues[field.name] = checkboxValues;
    }

    // returns values of all checkbox fields
    get allCheckboxValues() {
        return this.allCheckboxFieldValues;
    }

    // To check if at least one value of the checkbox group is chosen
    isCheckBoxValid(checkboxFieldValues) {
        if (checkboxFieldValues.includes(true)) {
            return true;
        }
        return false;
    }

    // Function which adds the validation properties to the field
    // Example: Displaying an error below the field when it is a required field,
    //          and surrounding the field with a red box.
    validateField(fieldConfig, form, submitButtonClicked) {
        
        const fieldName = fieldConfig.name;
        const formControl = form.controls[fieldName];
        //console.log(formControl);
        // We are validating the checkbox & terms fields separately because,
        // the validation of these fields are different than other fields.
        switch (fieldConfig.type) {

            case 'checkbox': {
                if(fieldConfig.validatorInfo.required == false){
                    $('#' + fieldName).removeClass('invalid-error');
                    return false;
                }
                const checkboxInvalid = !this.isCheckBoxValid(form.value[fieldName]);
                if (checkboxInvalid && (formControl.dirty || formControl.touched || submitButtonClicked)) {
                    $('#' + fieldName).addClass('invalid-error');
                    return true;
                } else {
                    $('#' + fieldName).removeClass('invalid-error');
                    return false;
                }
            }
            case 'terms': {
                if (!form.value[fieldName] && (formControl.dirty || formControl.touched || submitButtonClicked)) {
                    $('#' + fieldName).next().addClass('invalid-terms-error');
                    return true;
                } else {
                    $('#' + fieldName).next().removeClass('invalid-terms-error');
                    return false;
                }
            }
            case 'file': {
                if (
                    (this.fileUploadEvents[fieldName] || submitButtonClicked)
                    && fieldConfig.validatorInfo.required && !this.checkFileUploaded(fieldName)
                ) {
                    return true;
                } else {
                    return false;
                }
            }

            default: {
                if (formControl.invalid && (formControl.dirty || formControl.touched || submitButtonClicked)) {
                    $('#' + fieldName).addClass('invalid-error');
                    return true;
                } else {
                    $('#' + fieldName).removeClass('invalid-error');
                    return false;
                }
            }
        }
    }

    isAllUploadedFilesValid(fieldConfig) {
        let allUploadedFilesValid = true;
        const requiredFiles = fieldConfig
            .filter(config => config.type === 'file' && config.validatorInfo.required);

        requiredFiles.forEach(fileConfig => {
            if (this.checkFileUploaded(fileConfig.name)) {
                allUploadedFilesValid = allUploadedFilesValid && true;
            } else {
                allUploadedFilesValid = allUploadedFilesValid && false;
            }
        });

        return allUploadedFilesValid;
    }

    getErrorMessage(form, fieldConfig) {
        const formControl = form.controls[fieldConfig.name];
        let errorMessage = fieldConfig.label + ' is required.';

        switch (fieldConfig.type) {
            case 'terms': {
                errorMessage = 'Please accept terms before submitting the form.';
                break;
            }
            case 'file': {
                errorMessage = 'Please select a file';
                break;
            }
            case 'input': {
                if (fieldConfig.input_type === 'email' && formControl.invalid && formControl.errors.required) {
                    errorMessage = fieldConfig.label + ' is required.';
                } else if (fieldConfig.input_type === 'email' && formControl.invalid && formControl.errors.email) {
                    errorMessage = 'Enter a valid email address.';
                }
                break;
            }
        }

        return errorMessage;

    }

    fileUpload(uploadedFile, config) {
        //this.uploadedFiles[fieldName] = <File>uploadedFile;
        var file = <File>uploadedFile;

        var fileObject = $.grep(this.uploadedFiles, function (item, index) {
            return item.fieldName === config.name;
        })[0];

        if (fileObject) {
            fileObject.name = file.name;
            fileObject.size = file.size;
            fileObject.file = file;
        } else {
            fileObject = {
                name: file.name,
                size: file.size,
                file: file,
                fieldName: config.name,
                fieldLabel: config.label
            }

            this.fileUploadEvents[config.name] = true;
            this.uploadedFiles.push(fileObject);
        }
    }

    checkFileUploaded(fieldName) {
        var uploaded = this.uploadedFiles.some(file => file.fieldName === fieldName);

        return uploaded;
    }

    get allUploadedFiles() {
        return this.uploadedFiles;
    }

}
